import { Path, UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormMessage } from '../form/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../form/select';
import { SelectOption, SelectOptionGroup } from '@liquidbudget/common';

// Props type supporting both grouped and ungrouped options
type Props<T extends object, K extends string> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label?: string;
  options: SelectOption<K>[] | SelectOptionGroup<K>[];
  placeholder?: string;
  disabled?: boolean;
};

export const SelectField = <T extends object, K extends string>({
  form,
  name,
  label,
  options,
  placeholder = 'Select option',
  disabled = false,
}: Props<T, K>) => {
  // Check if we're dealing with grouped options
  const isGrouped = options.length > 0 && 'options' in options[0];

  // Get all options for icon check and selection
  const flatOptions = isGrouped
    ? (options as SelectOptionGroup<K>[]).flatMap((group) => group.options)
    : (options as SelectOption<K>[]);

  const hasIcons = flatOptions.some((option) => !!option.icon);
  const value = form.watch(name);
  const selectedOption = flatOptions.find((opt) => opt.value === value);

  const renderContent = () => {
    if (!selectedOption) return null;

    const Icon = selectedOption.icon;
    return (
      <div className="flex items-center">
        {hasIcons && Icon && <Icon className="mr-2 h-4 w-4" />}
        <span>{selectedOption.label}</span>
      </div>
    );
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <Select onValueChange={field.onChange} defaultValue={field.value} disabled={disabled}>
            <SelectTrigger className={disabled ? 'opacity-50 cursor-not-allowed' : ''}>
              <SelectValue placeholder={placeholder}>{value && renderContent()}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {isGrouped
                ? // Render grouped options
                  (options as SelectOptionGroup<K>[]).map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel className="text-slate-400 font-normal">{group.label}</SelectLabel>
                      {group.options.map(({ value, label, icon: Icon }) => (
                        <SelectItem key={value} value={value} disabled={disabled}>
                          <div className="flex items-center">
                            {Icon && <Icon className="mr-2 h-4 w-4" />}
                            <span>{label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))
                : // Render flat options
                  (options as SelectOption<K>[]).map(({ value, label, icon: Icon }) => (
                    <SelectItem key={value} value={value} disabled={disabled}>
                      <div className="flex items-center">
                        {Icon && <Icon className="mr-2 h-4 w-4" />}
                        <span>{label}</span>
                      </div>
                    </SelectItem>
                  ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
